import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { CaseHeader } from "@/components/admin/case-v2/CaseHeader";
import PendingUnlockRequestsStrip from "@/components/admin/PendingUnlockRequestsStrip";
import { ClinicalPathwayStepper } from "@/components/admin/case-v2/ClinicalPathwayStepper";
import { CaseTabsNav } from "@/components/admin/case-v2/CaseTabsNav";
import { OverviewTab } from "@/components/admin/case-v2/OverviewTab";
import { TreatmentTab } from "@/components/admin/case-v2/TreatmentTab";
import { FulfillmentTab } from "@/components/admin/case-v2/FulfillmentTab";
import { ActivityTab } from "@/components/admin/case-v2/ActivityTab";

/**
 * Admin Case Detail v2 — 4-tab redesign.
 *
 * iter101.93 · Ops tab retired. Every admin assignment / delivery
 * property (Planner / QA / Compliance Coach / Production Tech /
 * Priority / Target Date) now lives in the Overview right rail so
 * admin has ONE canonical view per record. `?tab=ops` deep-links
 * fall through to Overview (see ACTIVE_TABS below + the
 * `activeTab === "ops"` remap in the reader).
 *
 * Composes:
 *   <CaseHeader/>                ← identity, status/priority badges, CTA, overflow
 *   <ClinicalPathwayStepper/>    ← 7-stage horizontal pathway
 *   <CaseTabsNav/>               ← sticky tab nav (Overview / Treatment / Fulfillment / Activity)
 *   <Active tab>                 ← one of the 4 tabs above
 *
 * Tab persistence: kept in `?tab=` so a deep-linked URL takes the admin
 * back to where they were.
 *
 * Red-dot indicators (on the tab nav):
 *   • Overview     → has blockers
 *   • Treatment    → doctor approval still pending
 *   • Fulfillment  → material substitution flagged by tech
 *   • Activity     → (reserved for unread chat count)
 */
const ACTIVE_TABS = new Set(["overview", "treatment", "fulfillment", "activity"]);

export default function CaseDetailV2() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [c, setC] = useState(null);
    const [planners, setPlanners] = useState([]);
    const [qas, setQas] = useState([]);
    const [productionTechs, setProductionTechs] = useState([]);
    const [blockers, setBlockers] = useState([]);
    const [activeTab, setActiveTab] = useState(() => {
        const u = new URL(window.location.href);
        let t = u.searchParams.get("tab") || "overview";
        // iter101.93 · legacy `?tab=ops` deep links fall through to
        // Overview since Ops has been merged into the Overview right rail.
        if (t === "ops") t = "overview";
        return ACTIVE_TABS.has(t) ? t : "overview";
    });
    const [loading, setLoading] = useState(true);

    // Force-status modal state (kept here so the Header → overflow menu can fire it)
    const [forcing, setForcing] = useState(false);
    const [forceTarget, setForceTarget] = useState("");
    const [forceReason, setForceReason] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [caseRes, staffRes] = await Promise.all([
                api.get(`/cases/${id}`),
                api.get("/admin/staff"),
            ]);
            setC(caseRes.data);
            const staff = staffRes.data || [];
            setPlanners(staff.filter((x) => x.staff_type === "planner" && x.is_active !== false));
            setQas(staff.filter((x) => x.staff_type === "qa" && x.is_active !== false));
            setProductionTechs(staff.filter((x) => x.staff_type === "production" && x.is_active !== false));
            // Blockers live on a separate endpoint so we can re-fetch without
            // rehydrating the whole case object.
            try {
                const br = await api.get(`/admin/cases/${id}/blockers`);
                setBlockers(br.data?.blockers || []);
            } catch { setBlockers([]); }
        } catch {
            toast.error("Case not found");
            navigate("/admin/cases");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [id]);

    const setTab = (k) => {
        if (!ACTIVE_TABS.has(k)) k = "overview";
        setActiveTab(k);
        const u = new URL(window.location.href);
        u.searchParams.set("tab", k);
        window.history.replaceState(null, "", u.toString());
    };

    const sendToProduction = () => {
        // iter101.7 — Route header CTA to the "Route to Production" hero
        // banner on the Overview tab instead of hitting the legacy
        // /send-to-production endpoint (which required status === "approved"
        // and hard-coded assignee_name). The banner owns the canonical
        // assign-production flow now — pick a technician and confirm.
        if (activeTab !== "overview") setTab("overview");
        // Give the tab a tick to mount, then scroll the banner into view
        // and highlight the tech-picker so the admin's eye is drawn to it.
        setTimeout(() => {
            const banner = document.querySelector('[data-testid="route-to-production-banner"]');
            if (banner) {
                banner.scrollIntoView({ behavior: "smooth", block: "start" });
                banner.classList.add("ring-4", "ring-emerald-300");
                setTimeout(() => banner.classList.remove("ring-4", "ring-emerald-300"), 1800);
                document.querySelector('[data-testid="route-tech-select"]')?.focus();
            } else {
                // Fallback: focus the Ops-panel dropdown when the banner
                // isn't rendered (e.g. tech already assigned).
                document.querySelector('[data-testid="ops-production-select"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 120);
    };

    const submitForce = async () => {
        if (!forceTarget) { toast.error("Pick a status"); return; }
        if (!forceReason.trim() || forceReason.trim().length < 4) { toast.error("Reason must be at least 4 chars"); return; }
        try {
            await api.put(`/admin/cases/${id}/force-status`, { status: forceTarget, reason: forceReason.trim() });
            toast.success(`Status forced → ${forceTarget}`);
            setForcing(false);
            setForceTarget(""); setForceReason("");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    if (loading || !c) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-sm text-muted-foreground">
                Loading…
            </div>
        );
    }

    // Red-dot indicators per tab
    const treatmentPending = ["in_planning", "submitted_to_qa", "sent_for_approval", "revision_requested"].includes(c.status);
    const fulfillmentNeedsAttention = !!c.material_substitution_note;
    // iter101.12 · Phase 2 — Ops tab lights up whenever a staff-assignment
    // decision is pending: no planner on a CoE-planned case (post-submit
    // + pre-approval), no QA on a self-plan case awaiting QA review, or
    // no production tech on a case in a routing-ready state without one
    // yet.
    const opsPendingPlanner = c.planning_mode === "coe" && !c.planner_id
        && ["submitted", "planner_assigned"].includes(c.status);
    // iter101.50 — QA Reviewer requirement retired from the active
    // pipeline. Self-Plan cases in `pending_admin_review` no longer
    // wait on a QA assignment — admin can route them forward without
    // one. The `opsPendingQa` signal is silenced accordingly.
    const opsPendingQa = false;
    const opsPendingTech = ["approved", "awaiting_production_assignment", "submitted_to_production"].includes(c.status)
        && !c.production_tech_id;
    const tabDots = {
        overview:    (blockers || []).length > 0,
        treatment:   treatmentPending,
        fulfillment: fulfillmentNeedsAttention,
        ops:         opsPendingPlanner || opsPendingQa || opsPendingTech,
        activity:    false,
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto px-6 pt-6">
                <CaseHeader
                    c={c}
                    blockers={blockers}
                    onForceStatus={() => setForcing(true)}
                    onSendToProduction={sendToProduction}
                />
                {/* iter101.49 — Pending unlock requests from the planner
                    surface right below the case header so admin sees
                    them the moment they open the case. */}
                <PendingUnlockRequestsStrip caseId={id} onResolved={load} />
                <ClinicalPathwayStepper c={c} />
                <CaseTabsNav active={activeTab} onChange={setTab} dots={tabDots} />
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6">
                {activeTab === "overview" && (
                    <OverviewTab
                        c={c}
                        blockers={blockers}
                        planners={planners}
                        qas={qas}
                        productionTechs={productionTechs}
                        reload={load}
                        setTab={setTab}
                    />
                )}
                {activeTab === "treatment"   && <TreatmentTab c={c} />}
                {activeTab === "fulfillment" && <FulfillmentTab c={c} />}
                {activeTab === "activity"    && <ActivityTab c={c} reload={load} />}
            </div>

            {/* ── Force Status modal ─────────────────────────────────────── */}
            <Dialog open={forcing} onOpenChange={setForcing}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Force case status</DialogTitle>
                        <DialogDescription>
                            Override the normal workflow. Audit-logged with your reason. Use sparingly.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Target status</label>
                            <select
                                value={forceTarget}
                                onChange={(e) => setForceTarget(e.target.value)}
                                className="w-full mt-1 h-10 text-sm rounded-lg border border-border bg-background px-2"
                                data-testid="force-status-target"
                            >
                                <option value="">Select status…</option>
                                {["draft","submitted","in_planning","submitted_to_qa","sent_for_approval","approved","in_production","shipped","in_treatment","completed","cancelled"].map((s) =>
                                    <option key={s} value={s}>{s}</option>
                                )}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Reason</label>
                            <Textarea
                                value={forceReason}
                                onChange={(e) => setForceReason(e.target.value)}
                                rows={3}
                                placeholder="Why are you overriding the workflow?"
                                data-testid="force-status-reason"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setForcing(false)}>Cancel</Button>
                        <Button onClick={submitForce} className="bg-amber-600 hover:bg-amber-700" data-testid="force-status-submit">Force status</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
